import { useState } from 'react';
import { Link } from 'react-router';
import { Pager } from '@/components/common';
import { ROUTES } from '@/constants';
import { useLicence } from '@/services/queries';
import type { LicenceSeat } from '@/types';
import { StatusPill } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * Who holds the lab's seats, and on which machine.
 *
 * Transcribed from the design: a 16px card at 26px/28px, rows divided by a
 * hairline, a 28px rounded-square avatar, the name at 12.5px and the device in
 * 10.5px mono underneath.
 *
 * ── Why the device is in mono ───────────────────────────────────────────────
 *
 * Because it is a fingerprint, and the whole reason this column exists is
 * telling two of a lab's machines apart. Mono makes 4A91 and 4A9I different
 * shapes rather than a judgement call.
 *
 * ── Unclaimed seats are shown ───────────────────────────────────────────────
 *
 * The design lists "Seat 5 · not bound · Unassigned" as a row rather than
 * omitting it, and that is the right call: a lab that bought six seats and
 * bound four should see the two it is not using. Listing only bound seats makes
 * a half-provisioned licence look fully used.
 */

/** Initials for the avatar. Two letters, from the name the server holds. */
function initials(seat: LicenceSeat): string {
  if (!seat.holderName) return '—';
  const parts = seat.holderName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '—';
}

function SeatRow({ seat }: { seat: LicenceSeat }) {
  return (
    <div className="flex items-center gap-3 border-t border-border py-2.5">
      <span
        className="flex size-7 flex-none items-center justify-center rounded-lg bg-muted text-[11px] font-semibold text-brand-500"
        aria-hidden="true"
      >
        {initials(seat)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-medium">
          {seat.holderName ?? 'Unassigned seat'}
          {seat.isYou && <span className="ml-1.5 text-[10.5px] text-muted-foreground">(you)</span>}
        </div>
        <div className="truncate font-mono text-[10.5px] text-muted-foreground">
          {seat.deviceLabel ? `fp ${seat.deviceLabel}` : 'not bound'}
        </div>
      </div>
      <StatusPill status={seat.status} minWidth={92} />
    </div>
  );
}

/** Seats per page. Paged in the browser: the licence response carries them all. */
const PAGE_SIZE = 4;

export function AssignedSeats() {
  const query = useLicence();
  const [page, setPage] = useState(0);

  if (query.isPending) return <Skeleton className="h-64 w-full rounded-2xl" />;

  const licence = query.data?.licence ?? null;
  if (!licence) return null;

  // Clamped rather than trusted, so a licence that lost seats since the page
  // was turned shows its last page instead of an empty card.
  const lastPage = Math.max(0, Math.ceil(licence.seats.length / PAGE_SIZE) - 1);
  const current = Math.min(page, lastPage);
  const visible = licence.seats.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  return (
    <div className="rounded-2xl border border-border bg-card px-7 py-[26px] shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-[15px] font-bold whitespace-nowrap">Assigned seats</h2>
        <Link
          to={ROUTES.profile}
          className="text-xs font-semibold text-primary underline-offset-4 hover:underline"
        >
          Manage
        </Link>
      </div>

      {licence.seats.length === 0 ? (
        <p className="border-t border-border py-4 text-[12.5px] text-muted-foreground">
          No seats have been provisioned on this licence yet.
        </p>
      ) : (
        <>
          {visible.map((seat) => (
            <SeatRow key={seat.id} seat={seat} />
          ))}
          <Pager
            page={current}
            pageSize={PAGE_SIZE}
            total={licence.seats.length}
            onPage={setPage}
            className="border-t border-border pt-3"
          />
        </>
      )}
    </div>
  );
}
